import { gql } from '@apollo/client';
import { RUN_STATUS_FIELDS } from '@/graph/run-status';

/**
 * GraphQL operations for super-admin invocation of the staff-attribution
 * rollup. Both mutations are gated server-side via `requireSuperAdmin` in
 * `view-api/.../StaffAttributionAdmin.scala`; the client-side dialog
 * (`StaffAttributionAdminDialog`) additionally gates on
 * `useDebugMode().canBypassPermissions` (super-admin + debug mode) so the
 * trigger does not surface on any non-admin shell.
 *
 * Run progress is polled via `STAFF_ATTRIBUTION_RUN_STATUS` (shares the
 * `RUN_STATUS_FIELDS` fragment and the `useBackfillRunStatusPolling` hook
 * with the executive surface). The query aliases its field to `runStatus`
 * so the shared hook can read it uniformly.
 */

export const INVOKE_STAFF_ATTRIBUTION_COORDINATOR = gql`
  mutation InvokeStaffAttributionCoordinator($dryRun: Boolean!) {
    invokeStaffAttributionCoordinator(dryRun: $dryRun) {
      success
      runId
      message
    }
  }
`;

export const INVOKE_STAFF_ATTRIBUTION_BACKFILL = gql`
  mutation InvokeStaffAttributionBackfill(
    $backfillDays: Int!
    $backfillMode: String!
    $targetDate: String
    $dryRun: Boolean!
  ) {
    invokeStaffAttributionBackfill(
      backfillDays: $backfillDays
      backfillMode: $backfillMode
      targetDate: $targetDate
      dryRun: $dryRun
    ) {
      success
      runId
      message
    }
  }
`;

export const STAFF_ATTRIBUTION_RUN_STATUS = gql`
  query StaffAttributionRunStatus($runId: String!) {
    runStatus: staffAttributionRunStatus(runId: $runId) {
      ...RunStatusFields
    }
  }
  ${RUN_STATUS_FIELDS}
`;

export interface StaffAttributionInvokeResult {
  success: boolean;
  runId: string | null;
  message: string;
}

export interface InvokeStaffAttributionCoordinatorData {
  invokeStaffAttributionCoordinator: StaffAttributionInvokeResult;
}

export interface InvokeStaffAttributionCoordinatorVars {
  dryRun: boolean;
}

export interface InvokeStaffAttributionBackfillData {
  invokeStaffAttributionBackfill: StaffAttributionInvokeResult;
}

export type StaffAttributionBackfillMode = 'missing_only' | 'overwrite_all';

export interface InvokeStaffAttributionBackfillVars {
  backfillDays: number;
  backfillMode: StaffAttributionBackfillMode;
  /**
   * End date of the window (`yyyy-MM-dd`, org TZ). When `null`/omitted,
   * the Lambda defaults to yesterday in the org timezone — matching the
   * historical "backfillDays back from today" behavior.
   */
  targetDate: string | null;
  dryRun: boolean;
}
